<ng-template
  #label
  let-id="id"
  let-text="text"
  let-required="required"
  let-tip="tip"
  let-hint="hint"
>
  <div class="field-label">
    <label [attr.for]="id">{{ text }}</label>
    @if (tip) {
      <span class="info">
        <button
          type="button"
          class="info-button"
          [attr.aria-describedby]="id + '-tip'"
          [attr.aria-label]="'More information: ' + text"
        >
          <img src="assets/demands/info.svg" alt="" />
        </button>
        <span class="tooltip" role="tooltip" [id]="id + '-tip'">{{ tip }}</span>
      </span>
    }
    @if (required) {
      <span class="required" aria-hidden="true">*</span>
    }
    @if (hint) {
      <span class="field-hint">{{ hint }}</span>
    }
  </div>
</ng-template>

<main class="new-demand-page">
  <nav class="breadcrumb" aria-label="Breadcrumb">
    <a routerLink="/dashboard">Home</a>
    <img src="assets/demands/chevron-right.svg" alt="" />
    <a routerLink="/demands">My Demands</a>
    <img src="assets/demands/chevron-right.svg" alt="" />
    <span aria-current="page">New Demand</span>
  </nav>

  <h1 id="page-title" class="page-title">Request Demand</h1>

  <form
    class="demand-form"
    [formGroup]="form"
    (ngSubmit)="submit()"
    (keydown.enter)="preventImplicitSubmit($event)"
    novalidate
    aria-labelledby="page-title"
  >
    <section class="form-card" aria-labelledby="demand-type-title">
      <h2 id="demand-type-title">Demand Type</h2>

      <fieldset
        class="demand-type"
        [attr.aria-invalid]="showError('demandType')"
      >
        <legend class="field-label">
          <span class="legend-text">Select the demands type</span>
          <span class="info">
            <button
              type="button"
              class="info-button"
              aria-describedby="demand-type-tip"
              aria-label="More information: Select the demands type"
            >
              <img src="assets/demands/info.svg" alt="" />
            </button>
            <span class="tooltip" role="tooltip" id="demand-type-tip"
              >Select the option that best describes your need.</span
            >
          </span>
        </legend>

        <div class="selector-cards">
          <label class="selector-card">
            <input
              type="radio"
              formControlName="demandType"
              value="application-retirement"
            />
            <span class="selector-body">
              <span class="selector-title">Application Retirement</span>
              <span class="selector-text"
                >My Application requires Retirement. Decomissioning may include
                data archival.</span
              >
            </span>
          </label>
          <label class="selector-card">
            <input
              type="radio"
              formControlName="demandType"
              value="data-archival"
            />
            <span class="selector-body">
              <span class="selector-title">Data Archival</span>
              <span class="selector-text"
                >My data requires Archival without Application Retirement.</span
              >
            </span>
          </label>
        </div>
        @if (showError("demandType")) {
          <p class="field-error" role="alert">Select a demand type.</p>
        }
      </fieldset>

      <div class="section-heading">
        <h2>Application Information</h2>
        <p class="mandatory-note">*Mandatory Fields</p>
      </div>

      <div class="field-grid">
        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: { id: 'requestorName', text: 'Requestor Name' }
            "
          />
          <input
            id="requestorName"
            type="text"
            formControlName="requestorName"
            placeholder="Placeholder"
            [maxLength]="maxLength"
          />
          <div class="field-footer">
            <span class="counter"
              >{{ length("requestorName") }}/{{ maxLength }}</span
            >
          </div>
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'cmdbId',
                text: 'CMDB ID',
                required: !isDataArchival(),
                hint: 'Looking for CMDB Auto-populate',
              }
            "
          />
          <div class="cmdb-lookup-row">
            <input
              id="cmdbId"
              type="text"
              formControlName="cmdbId"
              placeholder="Placeholder"
              [maxLength]="maxLength"
              [attr.aria-invalid]="showError('cmdbId')"
              [attr.aria-required]="!isDataArchival()"
            />
            <button
              id="lookupCmdb"
              type="button"
              class="btn-outline"
              (click)="lookupProject()"
              [disabled]="lookupLoading() || !form.controls.cmdbId.value.trim()"
              [attr.aria-busy]="lookupLoading()"
            >
              {{ lookupLoading() ? "Looking up..." : "Retrieve" }}
            </button>
          </div>
          <div class="field-footer">
            <span class="helper"
              >Enter a CMDB ID and retrieve application details</span
            >
            <span class="counter">{{ length("cmdbId") }}/{{ maxLength }}</span>
          </div>
          @if (lookupLoading()) {
            <output class="lookup-status" aria-live="polite">
              Looking up application in CMDB...
            </output>
          }
          @if (lookupError()) {
            <p class="field-error" role="alert">
              Lookup failed. Check the CMDB ID and try again.
            </p>
          }
          @if (showError("cmdbId")) {
            <p class="field-error" role="alert">CMDB ID is required.</p>
          }
        </div>

        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'applicationName',
                text: isDataArchival()
                  ? 'Application Name / Collection of Records'
                  : 'Application Name',
                required: true,
              }
            "
          />
          <input
            id="applicationName"
            type="text"
            formControlName="applicationName"
            [placeholder]="
              lookupSucceeded() && !form.controls.applicationName.value
                ? 'Not available in CMDB'
                : 'Placeholder'
            "
            [maxLength]="maxLength"
            [readonly]="lookupSucceeded()"
            [attr.aria-readonly]="lookupSucceeded()"
            [attr.aria-invalid]="showError('applicationName')"
            aria-required="true"
          />
          <div class="field-footer">
            <span class="counter"
              >{{ length("applicationName") }}/{{ maxLength }}</span
            >
          </div>
          @if (showError("applicationName")) {
            <p class="field-error" role="alert">
              {{
                isDataArchival()
                  ? "Application name / collection of records is required."
                  : "Application name is required."
              }}
            </p>
          }
        </div>

        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'applicationDescription',
                text: 'Application Description',
                required: true,
                tip: 'Describe the application\'s primary purpose, business function, key users, and the data it manages or processes.',
              }
            "
          />
          <textarea
            id="applicationDescription"
            formControlName="applicationDescription"
            rows="4"
            [placeholder]="
              lookupSucceeded() && !form.controls.applicationDescription.value
                ? 'Not available in CMDB'
                : 'Placeholder'
            "
            [maxLength]="maxLength"
            [readonly]="lookupSucceeded()"
            [attr.aria-readonly]="lookupSucceeded()"
            [attr.aria-invalid]="showError('applicationDescription')"
            aria-required="true"
          ></textarea>
          <div class="field-footer">
            <span class="counter"
              >{{ length("applicationDescription") }}/{{ maxLength }}</span
            >
          </div>
          @if (showError("applicationDescription")) {
            <p class="field-error" role="alert">
              Application description is required.
            </p>
          }
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'agreedWithBusiness',
                text: 'Agreed with business',
                required: isApplicationRetirement(),
                tip: 'Has decommissioning/retirement of the application been Agreed with Business Owner/Data Owner?',
              }
            "
          />
          <div class="select-wrap">
            <select
              id="agreedWithBusiness"
              formControlName="agreedWithBusiness"
              [attr.aria-required]="isApplicationRetirement()"
              [attr.aria-invalid]="showError('agreedWithBusiness')"
            >
              <option value="" disabled hidden>Placeholder</option>
              @for (option of yesNoOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>
          @if (showError("agreedWithBusiness")) {
            <p class="field-error" role="alert">
              Agreed with business is required.
            </p>
          }
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: lookupSucceeded() ? 'businessUnit' : 'businessUnitSelect',
                text: 'Business Unit',
                required: true,
              }
            "
          />
          @if (lookupSucceeded()) {
            <input
              id="businessUnit"
              type="text"
              formControlName="businessUnit"
              [placeholder]="
                form.controls.businessUnit.value ? '' : 'Not available in CMDB'
              "
              readonly
              aria-readonly="true"
              [attr.aria-invalid]="showError('businessUnit')"
              aria-required="true"
            />
          } @else {
            <div class="select-wrap">
              <select
                id="businessUnitSelect"
                formControlName="businessUnit"
                [attr.aria-invalid]="showError('businessUnit')"
                aria-required="true"
              >
                <option value="" disabled hidden>Placeholder</option>
                @for (option of businessUnits; track option) {
                  <option [value]="option">{{ option }}</option>
                }
              </select>
            </div>
          }
          @if (showError("businessUnit")) {
            <p class="field-error" role="alert">Business unit is required.</p>
          }
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: lookupSucceeded()
                  ? 'itFunctionDepartment'
                  : 'itFunctionDepartmentSelect',
                text: 'IT Function Department',
                required: true,
              }
            "
          />
          @if (lookupSucceeded()) {
            <input
              id="itFunctionDepartment"
              type="text"
              formControlName="itFunctionDepartment"
              [placeholder]="
                form.controls.itFunctionDepartment.value
                  ? ''
                  : 'Not available in CMDB'
              "
              readonly
              aria-readonly="true"
              [attr.aria-invalid]="showError('itFunctionDepartment')"
              aria-required="true"
            />
          } @else {
            <div class="select-wrap">
              <select
                id="itFunctionDepartmentSelect"
                formControlName="itFunctionDepartment"
                [attr.aria-invalid]="showError('itFunctionDepartment')"
                aria-required="true"
              >
                <option value="" disabled hidden>Placeholder</option>
                @for (option of itFunctionDepartments; track option) {
                  <option [value]="option">{{ option }}</option>
                }
              </select>
            </div>
          }
          @if (showError("itFunctionDepartment")) {
            <p class="field-error" role="alert">
              IT function department is required.
            </p>
          }
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'businessOwner',
                text: 'Business Owner',
                required: true,
              }
            "
          />
          <input
            id="businessOwner"
            type="text"
            formControlName="businessOwner"
            [placeholder]="
              lookupSucceeded() && !form.controls.businessOwner.value
                ? 'Not available in CMDB'
                : 'Placeholder'
            "
            [maxLength]="maxLength"
            [readonly]="lookupSucceeded() && !form.controls.businessOwner.value"
            [attr.aria-readonly]="
              lookupSucceeded() && !form.controls.businessOwner.value
            "
            [attr.aria-invalid]="showError('businessOwner')"
            aria-required="true"
          />
          <div class="field-footer">
            <span class="counter"
              >{{ length("businessOwner") }}/{{ maxLength }}</span
            >
          </div>
          @if (showError("businessOwner")) {
            <p class="field-error" role="alert">Business owner is required.</p>
          }
        </div>

        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'itApplicationOwner',
                text: 'IT Application Owner/ IT System Owner',
                required: true,
              }
            "
          />
          <input
            id="itApplicationOwner"
            type="text"
            formControlName="itApplicationOwner"
            [placeholder]="
              lookupSucceeded() && !form.controls.itApplicationOwner.value
                ? 'Not available in CMDB'
                : 'Placeholder'
            "
            [maxLength]="maxLength"
            [readonly]="
              lookupSucceeded() && !form.controls.itApplicationOwner.value
            "
            [attr.aria-readonly]="
              lookupSucceeded() && !form.controls.itApplicationOwner.value
            "
            [attr.aria-invalid]="showError('itApplicationOwner')"
            aria-required="true"
          />
          <div class="field-footer">
            <span class="counter"
              >{{ length("itApplicationOwner") }}/{{ maxLength }}</span
            >
          </div>
          @if (showError("itApplicationOwner")) {
            <p class="field-error" role="alert">
              IT application owner is required.
            </p>
          }
        </div>
      </div>

      <div class="section-heading">
        <h2>Privacy &amp; Compliance</h2>
      </div>

      <div class="field-grid">
        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'sensitiveData',
                text: 'Privacy: Does the information include sensitive Personal Data/GDPR/CBDT requirements ?',
                required: true,
              }
            "
          />
          <div class="select-wrap">
            <select
              id="sensitiveData"
              formControlName="sensitiveData"
              [attr.aria-invalid]="showError('sensitiveData')"
              aria-required="true"
            >
              <option value="" disabled hidden>Placeholder</option>
              @for (option of yesNoOptions; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>
          @if (showError("sensitiveData")) {
            <p class="field-error" role="alert">This field is required.</p>
          }
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'informationClassification',
                text: 'Information Security/Clasification',
                required: true,
              }
            "
          />
          <div class="select-wrap">
            <select
              id="informationClassification"
              formControlName="informationClassification"
              [attr.aria-invalid]="showError('informationClassification')"
              aria-required="true"
            >
              <option value="" disabled hidden>Placeholder</option>
              @for (option of informationClassifications; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>
          @if (showError("informationClassification")) {
            <p class="field-error" role="alert">
              Information security classification is required.
            </p>
          }
        </div>

        <div class="field">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: lookupSucceeded() ? 'gxpRegulated' : 'gxpRegulatedSelect',
                text: 'Regulation: Is your data GxP?',
                required: true,
              }
            "
          />
          @if (lookupSucceeded()) {
            <input
              id="gxpRegulated"
              type="text"
              formControlName="gxpRegulated"
              [placeholder]="
                form.controls.gxpRegulated.value ? '' : 'Not available in CMDB'
              "
              readonly
              aria-readonly="true"
              [attr.aria-invalid]="showError('gxpRegulated')"
              aria-required="true"
            />
          } @else {
            <div class="select-wrap">
              <select
                id="gxpRegulatedSelect"
                formControlName="gxpRegulated"
                [attr.aria-invalid]="showError('gxpRegulated')"
                aria-required="true"
              >
                <option value="" disabled hidden>Placeholder</option>
                @for (option of yesNoOptions; track option) {
                  <option [value]="option">{{ option }}</option>
                }
              </select>
            </div>
          }
          @if (showError("gxpRegulated")) {
            <p class="field-error" role="alert">This field is required.</p>
          }
        </div>

        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'qualityRepresentative',
                text: 'Quality Representative name & contact details',
                hint: 'Conditional',
              }
            "
          />
          <input
            id="qualityRepresentative"
            type="text"
            formControlName="qualityRepresentative"
            [placeholder]="
              lookupSucceeded() && !form.controls.qualityRepresentative.value
                ? 'Not available in CMDB'
                : 'Placeholder'
            "
            [maxLength]="maxLength"
            [readonly]="lookupSucceeded()"
            [attr.aria-readonly]="lookupSucceeded()"
          />
          <div class="field-footer">
            <span class="helper">Auto-populate from CMDB where available</span>
            <span class="counter"
              >{{ length("qualityRepresentative") }}/{{ maxLength }}</span
            >
          </div>
        </div>

        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'country',
                text: 'Country',
                required: true,
                hint: 'Data origin, Users',
              }
            "
          />
          <div class="select-wrap">
            <select
              id="country"
              formControlName="country"
              [attr.aria-invalid]="showError('country')"
              aria-required="true"
            >
              <option value="" disabled hidden>Placeholder</option>
              @for (option of countries; track option) {
                <option [value]="option">{{ option }}</option>
              }
            </select>
          </div>
          <div class="field-footer">
            <span class="helper"
              >List all countries where data originates or is accessed by
              users</span
            >
          </div>
          @if (showError("country")) {
            <p class="field-error" role="alert">Country is required.</p>
          }
        </div>

        <div class="field full">
          <ng-container
            *ngTemplateOutlet="
              label;
              context: {
                id: 'additionalComments',
                text: 'Additional comments/Supporting Information',
              }
            "
          />
          <textarea
            id="additionalComments"
            formControlName="additionalComments"
            rows="4"
            placeholder="Placeholder"
            [maxLength]="maxLength"
          ></textarea>
          <div class="field-footer">
            <span class="counter"
              >{{ length("additionalComments") }}/{{ maxLength }}</span
            >
          </div>
        </div>
      </div>
    </section>

    <section class="form-card form-actions" aria-label="Demand actions">
      <button type="button" class="btn-outline" (click)="cancel()">
        Cancel
      </button>
      <div class="form-actions-end">
        <button type="button" class="btn-outline">Save draft</button>
        <button type="submit" class="btn-submit">Submit Intake</button>
      </div>
    </section>
  </form>
</main>
